import { useCompetitionRegistrationCategorySummaryQuery } from '~/pages/competition/set-category/queries';
import { useCompetitionDivisionListQuery } from '~/pages/competition/divisions/queries';
import { useCompetitionTeamListQuery } from '~/pages/competition/teams/queries';
import { Schema, resultSchema, teamResultSchema } from './result-form.schema';
import { isBenchpressCompetition } from '~/utils/competition-type';
import { useAuthentication } from '~/hooks/useAuthentication';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCreateResultsMutation } from '../mutations';
import { useCompetitionResultListQuery } from '../queries';
import { ResultWriteRequest } from '~/apis/ssf';
import { Resolver, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useState } from 'react';

interface Args {
  lifterId: number;
  competitionId?: number;
  isTeamComp?: boolean;
  /** Pre-selected (but still editable) competition team, when the lifter's team is already known. */
  teamId?: number;
  onFinish?: () => void;
}

/**
 * Shared state and submit logic for registering a single result, used by both the inline grid row
 * and the stacked single-lifter form. Team comps derive competition type / age category from the
 * selected team's division, so those are forced to null on submit and benchpress is resolved here.
 */
export function useResultForm({ lifterId, competitionId, isTeamComp = false, teamId, onFinish }: Args) {
  const { isSuperUser } = useAuthentication();
  const mutationResult = useCreateResultsMutation();
  const [selectedAgeLabel, setSelectedAgeLabel] = useState<string | undefined>();

  const form = useForm<Schema>({
    defaultValues: isTeamComp && teamId != null ? { team: teamId } : {},
    resolver: zodResolver(isTeamComp ? teamResultSchema : resultSchema) as unknown as Resolver<Schema>,
  });
  const { watch } = form;

  const { data: teams } = useCompetitionTeamListQuery(
    { competition: competitionId, page_size: 999 },
    isTeamComp && competitionId != null
  );
  const { data: divisions } = useCompetitionDivisionListQuery(
    { competition: competitionId, page_size: 999 },
    isTeamComp && competitionId != null
  );
  // Reuses the manage page's results query (same key) so the team-fullness check is served from cache.
  const { data: results } = useCompetitionResultListQuery(
    { competition: competitionId, page_size: 999 },
    isTeamComp && competitionId != null
  );

  function divisionOfTeam(selectedTeamId: number) {
    const divisionId = teams?.results.find(t => t.id === selectedTeamId)?.division.id;
    return divisions?.results.find(d => d.id === divisionId);
  }

  const { data: competitionCategories } = useCompetitionRegistrationCategorySummaryQuery(
    competitionId ?? 0,
    !isTeamComp && competitionId != null
  );

  const competitionType = watch('competition_type');
  const competitionTypeName = competitionCategories?.find(c => c.competition_type.id === Number(competitionType))
    ?.competition_type.name;
  const teamDivision = isTeamComp ? divisionOfTeam(Number(watch('team'))) : undefined;
  const isBenchpressCompetitionType =
    (competitionTypeName != null && isBenchpressCompetition(competitionTypeName)) ||
    (teamDivision != null && isBenchpressCompetition(teamDivision.competition_type.name));

  const isU1113Category = selectedAgeLabel === 'U11-13';

  async function onSubmit(data: Schema) {
    // A division caps lifters per team; reserves are substitutes, not extra slots, so they don't raise it.
    if (isTeamComp && data.team != null) {
      const division = divisionOfTeam(data.team);

      if (division) {
        const teamResultCount = results?.results.filter(r => r.team?.id === data.team).length ?? 0;
        if (teamResultCount >= division.lifters_per_team) {
          toast.error(`Laget är fullt. Divisionen tillåter max ${division.lifters_per_team} lyftare per lag.`);
          return;
        }
      }
    }

    // Benchpress competitions have no squat/deadlift; the fields are disabled, so force them to 0.
    const lifts = isBenchpressCompetitionType ? { squat: 0, deadlift: 0 } : {};
    const payload: ResultWriteRequest = isTeamComp
      ? { ...data, ...lifts, person: lifterId, competition_type: null, age_category: null }
      : { ...data, ...lifts, person: lifterId, competition_type: Number(competitionType) };
    await mutationResult.mutateAsync({ payload });
    toast.success('Resultatet har sparats');
    onFinish?.();
  }

  return {
    register: form.register,
    handleSubmit: form.handleSubmit,
    setValue: form.setValue,
    watch,
    formState: form.formState,
    onSubmit,
    isSuperUser,
    isPending: mutationResult.isPending,
    isBenchpressCompetitionType,
    isU1113Category,
    setSelectedAgeLabel,
  };
}
